import type { CSSProperties } from "react";
import { createLayoutStore } from "../cardResize/createLayoutStore";
import { cardGridStyleOf } from "../cardResize/cardResizeSpec";
import type { CardResizeSpec, CardSize } from "../cardResize/cardResizeSpec";

// Размерът на продуктовите кутийки в каталога на „Маркиране" (само десктоп,
// изглед „Плочки" — `CatalogGrid`; и в редактора на обедно меню): кутийките
// на ред и височината. Сменят се САМО с дръжките на избраната кутийка
// (`CardResizeGrips`) и се пазят на КОМПЮТЪРА, както размерите на масите
// (`tables/layout/tablesLayout.ts`) — localStorage["ordering_catalog_layout:<ресторант>"].
// Без запис — гридът отпреди дръжките: колкото се съберат по 205px, а
// височината — по съдържанието.

export type CatalogLayout = CardSize;

const CATALOG_LAYOUT_DEFAULT: CatalogLayout = { cardColumns: null, cardHeight: null };

/** Под тази ширина „− N +" застъпва цената (и в най-дребния кегел). */
export const CATALOG_CARD_MIN_WIDTH = 170;
export const CATALOG_CARD_MAX_COLUMNS = 8;
/** Един ред със „− N +" (32px) и отстоянията на кутийката. */
export const CATALOG_CARD_HEIGHT_MIN = 46;
export const CATALOG_CARD_HEIGHT_MAX = 240;
/** Под тази височина „− N +" застъпва единствения ред на името — всичко е
 *  на един ред (име · цена · грамаж или „− N +"). */
export const CATALOG_CARD_ONE_LINE_BELOW = 66;
/** Разстоянието между кутийките — `gap-1` на решетката. */
export const CATALOG_CARD_GAP = 4;

/**
 * Как кутийката подрежда съдържанието си (`ItemCard`):
 * - "content" — без избрана височина: както преди дръжките, редът на
 *   решетката е колкото най-високата кутийка в него;
 * - "fixed" — точно избраната височина: името взима мястото над цената (на
 *   колкото реда се събират — `useCatalogNameLines`, после „…");
 * - "oneLine" — ниска кутийка: името, цената и грамажът/„− N +" на един ред.
 */
export type CatalogCardSizing = "content" | "fixed" | "oneLine";

export const catalogCardSizingOf = (cardHeight: number | null): CatalogCardSizing =>
  cardHeight === null ? "content" : cardHeight < CATALOG_CARD_ONE_LINE_BELOW ? "oneLine" : "fixed";

/** Решетката с продуктите: N на ред, докато всяка е поне
 *  CATALOG_CARD_MIN_WIDTH (при по-тесен каталог колоните намаляват сами);
 *  без избор — колкото се съберат по 205px. С избрана височина всички
 *  кутийки са ЕДНАКВИ — редовете са точно толкова. */
export const catalogGridClassOf = ({ cardColumns, cardHeight }: CatalogLayout): string =>
  `grid gap-1 content-start ${cardHeight === null ? "" : "auto-rows-[var(--card-height)]"} ${
    cardColumns === null ? "grid-cols-[repeat(auto-fill,minmax(205px,1fr))]" : ""
  }`;

/** Избраните колони/височина са известни чак по време на работа → inline. */
export const catalogGridStyleOf = (layout: CatalogLayout): CSSProperties =>
  cardGridStyleOf(layout, CATALOG_CARD_MIN_WIDTH, CATALOG_CARD_GAP);

const clampOrNull = (value: unknown, min: number, max: number): number | null =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.max(min, Math.min(max, Math.round(value)))
    : null;

const store = createLayoutStore<CatalogLayout>({
  storagePrefix: "ordering_catalog_layout",
  defaults: CATALOG_LAYOUT_DEFAULT,
  normalize: (value) => ({
    cardColumns: clampOrNull(value?.cardColumns, 1, CATALOG_CARD_MAX_COLUMNS),
    cardHeight: clampOrNull(value?.cardHeight, CATALOG_CARD_HEIGHT_MIN, CATALOG_CARD_HEIGHT_MAX),
  }),
});

export const useCatalogLayout = store.useLayout;

/** Размерът, който ще стане при пускане на дръжката (null — не се влачи). */
export const useCatalogLayoutPreview = store.usePreview;

/** Дръжките на продуктовите кутийки (`CardResizeGrips`) и сивите контури
 *  при влачене (`CardLayoutPreview`) — като при масите. */
export const CATALOG_CARD_RESIZE: CardResizeSpec = {
  minWidth: CATALOG_CARD_MIN_WIDTH,
  maxColumns: CATALOG_CARD_MAX_COLUMNS,
  gap: CATALOG_CARD_GAP,
  heightMin: CATALOG_CARD_HEIGHT_MIN,
  heightMax: CATALOG_CARD_HEIGHT_MAX,
  preview: store.preview,
  commitPreview: store.commitPreview,
  reset: (patch) => store.update(patch, { persist: true }),
  labelKeys: {
    width: "ordering.tablesLayout.resizeCardWidth",
    height: "ordering.tablesLayout.resizeCardHeight",
    both: "ordering.tablesLayout.resizeCard",
  },
  // По целия десен/долен ръб — по средата на процепа между кутийките (4px) и
  // малко върху ръба им.
  gripPlacement: {
    width: "inset-y-0 -right-[9px] w-3.5 cursor-col-resize",
    height: "inset-x-0 -bottom-[9px] h-3.5 cursor-row-resize",
    both: "-right-[9px] -bottom-[9px] size-3.5 cursor-nwse-resize",
  },
  previewContentClassName:
    "[&_[data-item-card]]:border-transparent [&_[data-item-card]]:bg-transparent",
  previewContourClassName: "rounded-xl",
};
